import { UNITS, formatAmount, formatQuantityValue } from '@pos/business-logic';
import { Badge, Button, EmptyState, ErrorState, ListGroup, ListRow, Money, SearchField, Segmented, Skeleton, cn } from '@pos/ui';
import { ReceiptText } from 'lucide-react';
import { Fragment, useDeferredValue, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BranchChip } from '../../app/BranchChip';
import { Screen } from '../../app/Screen';
import { useSale, useSalesFeed, type SalesPreset } from '../../app/data';
import { METHOD_LABEL, dayHeader, when } from '../../lib/format';

const PRESETS: { value: SalesPreset; label: string }[] = [
  { value: 'today', label: 'Сегодня' },
  { value: 'yesterday', label: 'Вчера' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
];

const STATUS: Record<string, { label: string; tone: 'warning' | 'danger' } | undefined> = {
  PARTIALLY_REFUNDED: { label: 'Частичный возврат', tone: 'warning' },
  REFUNDED: { label: 'Возврат', tone: 'danger' },
};

export function SalesPage() {
  const [preset, setPreset] = useState<SalesPreset>('today');
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const feed = useSalesFeed(preset, query);
  const navigate = useNavigate();
  const summary = feed.data?.pages[0]?.summary;
  const items = feed.data?.pages.flatMap((p) => p.items) ?? [];

  let lastDay = '';
  return (
    <Screen title="Продажи">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Segmented label="Период" value={preset} onChange={setPreset} options={PRESETS} className="flex-1 [&>button]:flex-1" />
        </div>
        <div className="flex items-center gap-2">
          <SearchField
            size="md"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onClear={() => setQ('')}
            placeholder="Номер чека или клиент"
            aria-label="Поиск продаж"
            className="flex-1"
          />
        </div>
        <div className="flex justify-end">
          <BranchChip />
        </div>

        {summary && (
          <div className="grid grid-cols-3 gap-2 rounded-2xl border border-line bg-surface p-4">
            <div className="min-w-0">
              <p className="text-xs text-ink-3">Выручка</p>
              <p className="truncate text-lg font-semibold tnum">{formatAmount(summary.revenue)}</p>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-ink-3">Чеков</p>
              <p className="text-lg font-semibold tnum">{summary.count}</p>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-ink-3">Средний</p>
              <p className="truncate text-lg font-semibold tnum">{formatAmount(summary.average)}</p>
            </div>
          </div>
        )}

        {feed.isError ? (
          <ErrorState description="Не удалось загрузить продажи." onRetry={() => void feed.refetch()} />
        ) : feed.isPending ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface">
            <EmptyState icon={<ReceiptText />} title={query ? 'Ничего не найдено' : 'Продаж нет'} description={query ? 'Проверьте номер чека.' : 'Чеки со всех касс появятся здесь после синхронизации.'} />
          </div>
        ) : (
          <div>
            {items.map((s) => {
              const day = dayHeader(s.completedAt);
              const header = day !== lastDay;
              lastDay = day;
              const status = STATUS[s.status];
              return (
                <Fragment key={s.id}>
                  {header && <h2 className="mt-4 mb-2 px-1 text-sm font-medium text-ink-3 first:mt-0">{day}</h2>}
                  <button
                    type="button"
                    onClick={() => navigate(`/sales/${s.id}`)}
                    className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-left active:bg-sunken"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-md font-medium">
                        <span className="tnum">{s.number}</span>
                        {status && <Badge tone={status.tone}>{status.label}</Badge>}
                      </p>
                      <p className="truncate text-sm text-ink-3">
                        {when(s.completedAt)} · {s.itemCount} поз. · {s.methods.map((m) => METHOD_LABEL[m]).join(' + ')}
                        {s.customerName ? ` · ${s.customerName}` : ''}
                      </p>
                    </div>
                    <Money value={s.total - s.refundedTotal} className={cn('text-lg font-semibold', s.status === 'REFUNDED' && 'text-ink-4 line-through')} />
                  </button>
                </Fragment>
              );
            })}
            {feed.hasNextPage && (
              <Button className="mt-2 w-full" size="lg" loading={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
                Показать ещё
              </Button>
            )}
          </div>
        )}
      </div>
    </Screen>
  );
}

export function SaleDetailPage() {
  const { id } = useParams();
  const { data: s, isPending, isError, refetch } = useSale(id);
  return (
    <Screen title={s ? `Чек ${s.number}` : 'Чек'} back="/sales">
      {isError ? (
        <ErrorState description="Чек не найден или нет связи." onRetry={() => void refetch()} />
      ) : isPending || !s ? (
        <Skeleton className="h-72 rounded-lg" />
      ) : (
        <>
          <div className="mb-6 rounded-2xl border border-line bg-surface p-4">
            <p className="text-sm text-ink-3">{when(s.completedAt)}</p>
            <Money value={s.total} className="mt-1 block text-4xl font-bold tracking-[-0.02em]" />
            <p className="mt-2 text-sm text-ink-3">
              {s.branchName} · касса №{s.registerNumber} · {s.employeeName}
            </p>
            {s.customer && <p className="mt-1 text-sm text-ink-3">Покупатель: {s.customer.name}</p>}
          </div>

          <ListGroup title="Товары">
            {s.items.map((i) => (
              <ListRow
                key={i.id}
                title={i.name}
                subtitle={
                  <>
                    {formatQuantityValue(i.quantity)} {UNITS[i.unit].label} × {formatAmount(i.unitPrice)}
                    {i.discountAmount > 0 ? ` · скидка ${formatAmount(i.discountAmount)}` : ''}
                    {i.refundedQuantity > 0 ? <span className="text-warning"> · возвращено {formatQuantityValue(i.refundedQuantity)}</span> : null}
                    {i.serialNumber ? ` · ${i.serialNumber}` : ''}
                  </>
                }
                trailing={<span className="font-medium tnum">{formatAmount(i.total)}</span>}
              />
            ))}
          </ListGroup>

          <ListGroup title="Итого">
            <ListRow title="Сумма товаров" trailing={<span className="tnum">{formatAmount(s.subtotal)}</span>} />
            {s.discountTotal > 0 && <ListRow title="Скидка" trailing={<span className="text-success tnum">−{formatAmount(s.discountTotal)}</span>} />}
            {s.taxTotal > 0 && <ListRow title="В т. ч. НДС" trailing={<span className="tnum">{formatAmount(s.taxTotal)}</span>} />}
            <ListRow title={<span className="font-semibold">К оплате</span>} trailing={<span className="font-semibold tnum">{formatAmount(s.total)}</span>} />
          </ListGroup>

          <ListGroup title="Оплата">
            {s.payments.map((p, i) => (
              <ListRow
                key={i}
                title={METHOD_LABEL[p.method]}
                subtitle={p.method === 'CASH' && p.receivedAmount && p.receivedAmount > p.amount ? `Получено ${formatAmount(p.receivedAmount)}, сдача ${formatAmount(p.receivedAmount - p.amount)}` : (p.reference ?? undefined)}
                trailing={<span className="tnum">{formatAmount(p.amount)}</span>}
              />
            ))}
          </ListGroup>

          {s.returns.length > 0 && (
            <ListGroup title="Возвраты">
              {s.returns.map((r) => (
                <ListRow key={r.id} title={r.number} subtitle={[when(r.createdAt), r.reason].filter(Boolean).join(' · ')} trailing={<span className="text-danger tnum">−{formatAmount(r.total)}</span>} />
              ))}
            </ListGroup>
          )}
        </>
      )}
    </Screen>
  );
}
